import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";

/**
 * "Pairs Well With" cross-sell — fetches a complementary product and offers
 * a one-click add-to-cart. Currently configured to attach Hospira Bacteriostatic
 * Water to every peptide PDP (skipped on the bac-water PDP itself).
 *
 * Props:
 *  - currentSlug: slug of the product being viewed (so we don't recommend itself)
 *  - currentCategory: category of the current product (used to skip on Lab Supplies)
 */
export default function PairsWellWith({ currentSlug, currentCategory }) {
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  const PAIR_SLUG = "bacteriostatic-water";

  useEffect(() => {
    // Skip when the current product doesn't logically pair with bac water:
    // - The bac-water product itself
    // - Anything in Lab Supplies (already the diluent category)
    // - Anything in ProSource Mist (ready-to-use nasal sprays don't need reconstitution)
    // - Anything in Skincare (cosmetic products don't reconstitute)
    if (
      currentSlug === PAIR_SLUG ||
      currentCategory === "Lab Supplies" ||
      currentCategory === "ProSource Mist" ||
      currentCategory === "Skincare"
    ) return;
    api
      .get(`/products/${PAIR_SLUG}`)
      .then(({ data }) => {
        setProduct(data);
        setVariant(data.variants?.[0] || null);
      })
      .catch(() => setProduct(null));
  }, [currentSlug, currentCategory]);

  if (!product || !variant) return null;

  const onAdd = () => {
    addItem(product, variant, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  return (
    <div className="mt-12" data-testid="pairs-well-with-section">
      <div className="overline mb-4">PAIRS WELL WITH</div>
      <div className="surface p-5 flex items-center gap-5">
        <Link
          to={`/product/${product.slug}`}
          className="block w-20 h-20 vial-bg flex items-center justify-center flex-shrink-0"
          data-testid="pairs-well-image-link"
        >
          <img
            src={product.image}
            alt={product.name}
            className="w-[80%] h-[80%] object-contain"
            data-testid={`product-image-${product.slug}`}
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-[#5C657A]">
            {product.category}
          </div>
          <Link
            to={`/product/${product.slug}`}
            className="font-heading text-lg text-white hover:text-[#00C3FF] transition-colors block mt-0.5"
            data-testid="pairs-well-name"
          >
            {product.name}
          </Link>
          <div className="text-[11px] text-[#8A94A6] mt-1 leading-snug">
            Sterile diluent standard for lyophilized peptide research supplies.
          </div>
          <div className="font-mono-tech text-xs text-[#00C3FF] mt-2">
            From {`$${Number(variant.price).toFixed(2)}`} · {variant.size}
          </div>
        </div>
        <button
          onClick={onAdd}
          disabled={added}
          className="btn-ghost flex-shrink-0"
          style={{ padding: "12px 18px" }}
          data-testid="pairs-well-add-button"
        >
          {added ? <Check size={14} /> : <Plus size={14} />}
          {added ? "Added" : "Add"}
        </button>
      </div>
    </div>
  );
}
